import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Newspaper, CalendarHeart, FileText, ArrowRight, CalendarDays, MapPin, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { api, resolveMedia } from "../../lib/api";
import { useLang } from "../../i18n";
import { Reveal, SectionHeading } from "../Reveal";

export function BannerCarousel() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/banners").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;
  const b = items[idx];

  return (
    <section data-testid="banner-carousel" className="relative bg-ngo-forest overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={b.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute inset-0">
            <img src={resolveMedia(b.image)} alt={b.title} className="w-full h-full object-cover opacity-35" />
            <div className="absolute inset-0 bg-gradient-to-r from-ngo-forest via-ngo-forest/75 to-ngo-forest/30" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 flex flex-col sm:flex-row items-center gap-8">
            <div className="flex-1">
              <h2 data-testid="banner-title" className="font-heading font-bold text-3xl sm:text-4xl text-ngo-cream leading-tight">{b.title}</h2>
              {b.description && <p className="mt-4 text-ngo-mint/80 text-base md:text-lg leading-relaxed max-w-xl">{b.description}</p>}
              {b.button_text && (
                <Link to={b.button_link || "/donate"} data-testid="banner-cta" className="mt-7 inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-ngo-gold text-ngo-ink font-bold hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(216,169,40,0.4)] transition-[transform,box-shadow] duration-300">
                  {b.button_text} <ArrowRight size={16} />
                </Link>
              )}
            </div>
            <img src={resolveMedia(b.image)} alt={b.title} className="hidden md:block w-80 h-52 object-cover rounded-2xl shadow-2xl border border-ngo-mint/20" />
          </div>
        </motion.div>
      </AnimatePresence>
      {items.length > 1 && (
        <div className="absolute bottom-4 right-5 flex gap-2">
          <button data-testid="banner-prev" onClick={() => setIdx((idx - 1 + items.length) % items.length)} aria-label="पिछला बैनर" className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 transition-colors duration-200"><ChevronLeft size={16} /></button>
          <button data-testid="banner-next" onClick={() => setIdx((idx + 1) % items.length)} aria-label="अगला बैनर" className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 transition-colors duration-200"><ChevronRight size={16} /></button>
        </div>
      )}
    </section>
  );
}

export function LatestContent() {
  const { pick } = useLang();
  const [blogs, setBlogs] = useState([]);
  const [events, setEvents] = useState([]);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    api.get("/blogs?limit=3").then((r) => setBlogs(r.data.items || [])).catch(() => {});
    api.get("/events").then((r) => setEvents((r.data.items || []).filter((e) => e.status === "upcoming").slice(0, 3))).catch(() => {});
    api.get("/documents").then((r) => setDocs((r.data.items || []).slice(0, 4))).catch(() => {});
  }, []);

  if (!blogs.length && !events.length && !docs.length) return null;

  return (
    <>
      {events.length > 0 && (
        <section data-testid="latest-events-section" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <SectionHeading overline={pick({ hi: "जुड़िए", en: "Join In" })} title={pick({ hi: "आगामी कार्यक्रम", en: "Upcoming Events" })} testid="latest-events-heading" />
              <Link to="/events" data-testid="latest-events-all" className="group inline-flex items-center gap-2 text-sm font-bold text-ngo-deep hover:gap-3 transition-[gap] duration-200">
                {pick({ hi: "सभी कार्यक्रम", en: "All Events" })} <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {events.map((ev, i) => (
                <Reveal key={ev.id} i={i}>
                  <article data-testid={`latest-event-${i}`} className="h-full bg-ngo-cream rounded-2xl border border-ngo-deep/10 p-6 hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(23,107,58,0.12)] transition-[box-shadow,transform] duration-300">
                    <div className="w-11 h-11 rounded-xl bg-ngo-mint flex items-center justify-center text-ngo-deep"><CalendarHeart size={20} /></div>
                    <h3 className="mt-4 font-heading font-bold text-lg text-ngo-ink leading-snug">{ev.title}</h3>
                    <div className="mt-2.5 space-y-1 text-sm text-ngo-ink/60">
                      {ev.date && <p className="flex items-center gap-2"><CalendarDays size={14} className="text-ngo-leaf" /> {new Date(ev.date).toLocaleDateString("hi-IN", { day: "numeric", month: "long", year: "numeric" })}</p>}
                      {ev.location && <p className="flex items-center gap-2"><MapPin size={14} className="text-ngo-leaf" /> {ev.location}</p>}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {blogs.length > 0 && (
        <section data-testid="latest-blogs-section" className="py-20 bg-ngo-mint/50 grain relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <SectionHeading overline={pick({ hi: "समाचार", en: "News" })} title={pick({ hi: "ताज़ा ब्लॉग एवं समाचार", en: "Latest Blogs & News" })} testid="latest-blogs-heading" />
              <Link to="/blog" data-testid="latest-blogs-all" className="group inline-flex items-center gap-2 text-sm font-bold text-ngo-deep hover:gap-3 transition-[gap] duration-200">
                {pick({ hi: "सभी ब्लॉग", en: "All Blogs" })} <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {blogs.map((b, i) => (
                <Reveal key={b.id} i={i}>
                  <Link to={`/blog/${b.slug}`} data-testid={`latest-blog-${i}`} className="group block h-full bg-white rounded-2xl border border-ngo-deep/10 overflow-hidden hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(23,107,58,0.12)] transition-[box-shadow,transform] duration-300">
                    {b.featured_image ? (
                      <img src={resolveMedia(b.featured_image)} alt={b.title} loading="lazy" className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-40 bg-ngo-forest/90 flex items-center justify-center text-ngo-gold"><Newspaper size={30} /></div>
                    )}
                    <div className="p-5">
                      {b.published_at && <p className="text-xs text-ngo-ink/50">{new Date(b.published_at).toLocaleDateString("hi-IN", { day: "numeric", month: "long", year: "numeric" })}</p>}
                      <h3 className="mt-2 font-heading font-bold text-ngo-ink leading-snug group-hover:text-ngo-deep transition-colors duration-200">{b.title}</h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {docs.length > 0 && (
        <section data-testid="latest-docs-section" className="py-20 bg-ngo-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <SectionHeading overline={pick({ hi: "पारदर्शिता", en: "Transparency" })} title={pick({ hi: "नवीनतम रिपोर्ट एवं दस्तावेज़", en: "Latest Reports & Documents" })} testid="latest-docs-heading" />
              <Link to="/reports" data-testid="latest-docs-all" className="group inline-flex items-center gap-2 text-sm font-bold text-ngo-deep hover:gap-3 transition-[gap] duration-200">
                {pick({ hi: "सभी दस्तावेज़", en: "All Documents" })} <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {docs.map((d, i) => (
                <Reveal key={d.id} i={i}>
                  <div data-testid={`latest-doc-${i}`} className="flex items-center gap-4 bg-white rounded-2xl border border-ngo-deep/10 p-5 hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(23,107,58,0.10)] transition-[box-shadow,transform] duration-300">
                    <div className="w-11 h-11 rounded-xl bg-ngo-forest flex items-center justify-center text-ngo-gold shrink-0"><FileText size={19} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-ngo-ink truncate">{d.title}</p>
                      <p className="text-xs text-ngo-ink/50 mt-0.5">{[d.year, d.category].filter(Boolean).join(" • ")}</p>
                    </div>
                    <a data-testid={`latest-doc-view-${i}`} href={resolveMedia(d.file)} target="_blank" rel="noopener noreferrer" aria-label="PDF देखें" className="w-10 h-10 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-deep hover:text-white transition-colors duration-200">
                      <Eye size={16} />
                    </a>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
